import { Checkbox as MuiCheckbox, CheckboxProps, styled, Stack, FormControlLabel, Typography, Box, css, SxProps, Theme } from "@mui/material";
import { ChangeEvent, FC, ReactNode } from "react";

import { useFormField } from "@/modules/irohana-components/elements/FormField";
import { IconCheckboxChecked, IconCheckboxUnchecked } from "@/modules/irohana-components/assets/icons";
import { borderColor, button, state, text } from "@/modules/irohana-components/theme/colors-new";

export type TCheckboxNewProps = {
  label?: ReactNode;
  subLabel?: string;
  nonControl?: boolean;
  prefixLabel?: ReactNode;
  error?: boolean;
  readOnly?: boolean;
  labelColor?: string;
  subLabelColor?: string;
  alignStart?: boolean;
  labelSx?: SxProps<Theme>;
  formControlLabelSx?: SxProps<Theme>;
} & CheckboxProps;

export const CheckboxNew: FC<TCheckboxNewProps> = ({
  label,
  subLabel,
  nonControl = false,
  prefixLabel,
  error,
  readOnly = false,
  labelColor = text.primary,
  subLabelColor = text.secondary,
  alignStart = true,
  labelSx,
  formControlLabelSx,
  size = "small",
  ...rest
}) => {
  const { field, fieldState } = useFormField(nonControl);
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    field?.onChange(e);
    field?.onBlur();
    rest.onChange && rest.onChange(e, e.target.checked);
  };

  const checked = !!rest.checked || !!field?.value;
  const iconSize = size === "small" ? 18 : 24;

  return (
    <CheckboxWrap alignStart={alignStart}>
      <FormControlLabel
        sx={formControlLabelSx}
        label={
          <Stack flexDirection="row" gap={0.5}>
            {prefixLabel && (
              <Typography variant="body14" color={text.primary}>
                {prefixLabel}
              </Typography>
            )}
            <Stack gap={0.5}>
              {label && (
                <Typography variant="body14" sx={labelSx} color={labelColor}>
                  {label}
                </Typography>
              )}
              {subLabel && (
                <Typography variant="cap12" color={subLabelColor}>
                  {subLabel}
                </Typography>
              )}
            </Stack>
          </Stack>
        }
        control={
          <StyledCheckbox
            error={Boolean(error) || Boolean(fieldState?.error)}
            readOnly={readOnly}
            size={size}
            icon={
              <Stack p="3px">
                <IconCheckboxUnchecked width={iconSize} height={iconSize} />
              </Stack>
            }
            checkedIcon={
              <Stack p="3px">
                <IconCheckboxChecked width={iconSize} height={iconSize} />
              </Stack>
            }
            {...rest}
            {...field}
            checked={checked}
            onChange={readOnly ? undefined : handleChange}
          />
        }
      />
    </CheckboxWrap>
  );
};

const CheckboxWrap = styled(Box, { shouldForwardProp: (prop) => prop !== "alignStart" })<{ alignStart: boolean }>`
  .MuiFormControlLabel-root {
    align-items: ${({ alignStart }) => (alignStart ? "flex-start" : "center")};
    gap: 12px;
    margin: 0;
  }
`;

const options = { shouldForwardProp: (propName: string) => !["error", "readOnly"].includes(propName) };

const StyledCheckbox = styled(MuiCheckbox, options)<{ error: boolean; readOnly: boolean }>`
  width: 24px;
  height: 24px;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    top: -1px;
    left: -1px;
    right: -1px;
    bottom: -1px;
    border: 2px solid transparent;
    border-radius: 4px;
    pointer-events: none;
  }
  &:focus-visible::after,
  &.Mui-focusVisible::after {
    border-color: ${borderColor.orange};
  }
  &.Mui-focusVisible {
    .MuiTouchRipple-root {
      display: none;
    }
  }
  ${({ error }) =>
    error
      ? css`
          color: ${state.error_1}!important;
        `
      : css`
          color: ${button.primary}!important;
        `};
  &.Mui-disabled {
    color: ${button.disable}!important;
  }
  ${({ readOnly }) =>
    readOnly &&
    css`
      color: #abb4b6 !important;
      pointer-events: none;
    `};
`;
